import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from "react-icons/md";
import { BsBank } from "react-icons/bs";
import { HiOutlineReceiptTax } from "react-icons/hi";
import { FaRegBuilding } from "react-icons/fa";
import { FaUserCircle } from "react-icons/fa";
import { MdAccountCircle } from "react-icons/md";
import {Layout, CategoryProfile, CategoryConfig} from '../../../../components';
import {handleApi,inputDate} from '../../../../utils/utils';
import AppContext from '../../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ViewCategory = () => {
  const location = useLocation();
  const initialCategory = location.state?.initialCategory;
  
  // FIX: Initialize react-hook-form directly with defaultValues from initialCategory.
  // This removes the need for a separate 'product' state variable that was causing conflicts.
  const { reset, control, register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: initialCategory
  });
  
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [displayImg, setDisplayImg] = useState(''); 
  const [imgType, setImgType] = useState('');
  const { userData, companyProfile, bank, currentColor, actionCount, setActionCount } = useContext(AppContext);

  useEffect(() => {fetchDepartments();}, []); 
  
  // FIX: This useEffect now only runs once on component mount to handle the initial photo.
  // It is no longer a dependency on 'product' state, which was causing the reset loop.
  useEffect(() => {
    if (initialCategory?.photo_url) {
      fetchImageAsBase64(initialCategory.photo_url);
    } else if (initialCategory?.photo?.data && initialCategory?.photo?.contentType) {
      setDisplayImg(initialCategory.photo.data);
      setImgType(initialCategory.photo.contentType);
    } else {
      setDisplayImg('');
      setImgType('');
    }
  }, [initialCategory]);
  
  // New function to fetch image from URL and convert to base64
  const fetchImageAsBase64 = async (url) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result.split(',')[1];
        const contentType = blob.type;
        setDisplayImg(base64data);
        setImgType(contentType);
      };
    } catch (error) {
      console.error("Failed to fetch image from URL", error);
      setDisplayImg('');
      setImgType('');
    } finally {
      setIsImageLoading(false);
    }
  };
  
  const fetchDepartments = async () => {
    const apiEndpoint = 'departments';
    const apiMethod = 'GET';
    const parameters = {};
    setIsLoading(true);
    const params = {};
    try {
      const response = await handleApi(apiEndpoint, params, userData, apiMethod, parameters);
      if (response.error) {
        console.error('Error fetching departments', response.errorMessage);
        return;
      }
      const { status, statusText } = response;
      if (status === 200) {
        const fetchedDepartments = response.data;
        const transformedDepartments = fetchedDepartments.map(department => ({
          value: department.id,
          label: department.name
        }));
    
        setDepartments(transformedDepartments);
      } else {
        console.error(`Error ${status}: ${statusText}`);
      }
    } catch (error) {
      console.error(error.message || 'An unexpected error occurred.');
    } finally {setIsLoading(false);}
  };
  
  const handleCancel = () => {
    // FIX: reset form and image states to initial values
    reset(initialCategory);
    if (initialCategory?.photo_url) {
        fetchImageAsBase64(initialCategory.photo_url);
    } else {
        setDisplayImg('');
        setImgType('');
    }
  };

  const navigate = useNavigate(); 

  const handleClosePage = () => {
   navigate(-1);
  };

  const convertFile = (files) => {
    if (files && files.length > 0 && files[0] instanceof File) {
      const fileRef = files[0];
      const fileType = fileRef.type;
      const reader = new FileReader();
      reader.readAsBinaryString(fileRef);
      reader.onload = (ev) => {
        const base64Data = btoa(ev.target.result);
        setDisplayImg(base64Data);
        setImgType(fileType);
        // FIX: Remove the setProduct call to prevent the useEffect from triggering a reset.
      };
    }
  };

  const handleImgChange = (event) => {
    const { name, files } = event.target;
    if (name === "logo") {convertFile(files);}
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
  };
  
  const handleProductProfileFormSubmit = async (dataFromForm) => {
    try {
    //   console.log('Form data received from react-hook-form (safe stringify):', JSON.stringify(dataFromForm, null, 2));
    } catch (e) {
      console.error("Failed to stringify `dataFromForm`. Circular reference found:", dataFromForm);
      toast.error("Form data is corrupted. Check the console for details.");
      setIsLoading(false);
      return;
    }
 
    const payload = {    // Create payload 
        id: dataFromForm.id,
        first_name: dataFromForm.first_name,
        last_name: dataFromForm.last_name,
        salutation: dataFromForm.salutation,
        gender:dataFromForm.gender,
        job_title: dataFromForm.job_title,
        email: dataFromForm.email,
        phone_number: dataFromForm.phone_number,
        department_id: dataFromForm.department_id,
        hire_date: inputDate(dataFromForm.hire_date),
        date_of_birth: inputDate(dataFromForm.date_of_birth),
      };
      
      // Add the photo data if it exists
    if (displayImg && imgType) {
        payload.photo = {data: displayImg,contentType: imgType};
    } else { payload.photo = null;}
      
    try {
      console.log('Final payload to send to API (safe stringify):', JSON.stringify(payload, null, 2));
    } catch (e) {
      console.error("Final payload has a circular reference. Inspect the object below.", payload);
      toast.error("The final payload is corrupted. Check the console for details.");
      setIsLoading(false);
      return;
    }

    const apiMethod = payload.id ? 'PUT' : 'POST';
    const parameters = { ...(payload.id && { id: payload.id }) };
    setIsLoading(true);
    try {
      const response = await handleApi('products', payload, userData, apiMethod, parameters);
      if (response.status === 200 || response.status === 201) {
        toast.success('Product profile saved successfully!');
        // FIX: The component will now display the updated data without a full reset.
        // The parent state no longer needs to be explicitly updated here.
      } else {
        toast.error(`Error saving product: ${response.statusText || 'An unknown error occurred'}`);
      }
    } catch (error) {
      toast.error('Error saving product: ' + error.message);
    } finally {
      setActionCount(actionCount + 1);
      setIsLoading(false);
    }
  };
  // The SVG path below represents a 2D, open-top box outline.
  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="${currentColor}" width="100" height="100">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6m6 0a2.25 2.25 0 012.25-2.25h2.25A2.25 2.25 0 0121 6m-18 0v1.5c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125V6M3 6h18m-18 8.25V18a2.25 2.25 0 002.25 2.25h13.5A2.25 2.25 0 0021 18v-3.75m-18 0V12h18v2.25" />
  </svg>`;
  const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
  const defaultPhotoUrl = `data:image/svg+xml;base64,${base64Svg}`;


  const photoUrl = isImageLoading ? null : (displayImg && imgType ? `data:${imgType};base64,${displayImg}` : defaultPhotoUrl);
  return (
    <Layout>
       <div className="min-h-screen text-white p-2 sm:p-4">
        <div className="relative flex justify-between items-center">
          <h1 className="text-center font-bold flex-grow text-black font-lg">CATEGORY PREVIEW</h1>
          {/* Action Button Group (NEW) */}
          

          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300 shadow-md"
              aria-label="Go back"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          </div>
        </div>
        
        <ToastContainer position="top-center" autoClose={2000} />

        {isLoading ? (
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Loading. Please wait...</p>
          </div>
        ) : (

          <div className="mx-auto border-b border-gray-400 bg-gray-500 transition-all duration-200 cursor-pointer rounded-xl shadow-2xl p-6 w-fit">
         
              <CategoryProfile
                  fields={CategoryConfig}
                  control={control}
                  register={register}
                  photo={photoUrl}
                  onChange={handleChange}
                  departments={departments}
                  handleImgChange={handleImgChange}
                  onSubmit={handleSubmit(handleProductProfileFormSubmit)}
                  categoryData={initialCategory} // FIX: Pass initialCategory here instead of a mutable state variable.
                  onCancel={handleCancel}
                  errors={errors}
                  reset={reset}
                  isLoading={isLoading}
                  isReadOnly={true} //Pass the boolean true .
                />
              </div>
        )}
      </div>
    </Layout>
  );
};
export default ViewCategory;
